import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { showToast } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  getItemCardTrackingParams,
  isEven,
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  buildPromoUpsellParams,
  buildVoucherUpsellParams,
  getVoucherTrackingParams,
  hasCountVouchers,
  useAddToCart,
  usePromoVoucherPreviewEnabled,
  useStockNumberInput,
  useVoucherDisplayEnabled,
} from '@diamond/sol-buyer/utils';
import {
  useCartStore,
  useCartTotalProductDetail,
  useProductVoucherEligibilityList,
} from '@diamond/sol-buyer-context';
import { dinero, toDecimal } from 'dinero.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CardProductProps } from '../card-product/card-product';
import { buildProductPromoOffers } from '../promo-preview';
import { ProductInfoSection } from './ProductInfoSection';
import { PromoSection } from './PromoSection';
import { SuccessContent } from './SuccessContent';
import { TotalPriceSection } from './TotalPriceSection';
import { VoucherSection } from './VoucherSection';

interface AddToCartPopupProps {
  isOpen: boolean;
  onClose: () => void;
  product?: CardProductProps['product'];
  productId: string;
  token: string;
  selectedShipToId: string;
  onSuccess?: () => void;
  section?: string;
  page?: string;
  sectionId?: string;
  itemIndex?: number;
  sectionIndex?: string;
  defaultQuantity?: number;
  autoSelectProductId?: string;
  enableCartStoreSync?: boolean;
  shouldAutoSelectOnAdd?: boolean;
  onSyncStart?: () => void;
  onSyncComplete?: () => void;
}

export const AddToCartPopup: React.FC<AddToCartPopupProps> = ({
  isOpen,
  onClose,
  product,
  productId,
  token,
  selectedShipToId,
  onSuccess,
  section,
  page,
  sectionId,
  itemIndex,
  sectionIndex,
  defaultQuantity,
  autoSelectProductId,
  enableCartStoreSync = false,
  shouldAutoSelectOnAdd = false,
  onSyncStart,
  onSyncComplete,
}) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const toast = useToast();

  const isCartRecommendationEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_CART_RECOMMENDATION,
    false
  );
  const addedItemCode = product?.item_code
    ? Number(product.item_code)
    : undefined;

  const [magnifiedQty, setMagnifiedQty] = useState<number>(0);
  const [uiQuantity, setUiQuantity] = useState<number>(0);

  const orderQtyMultiple = Number(
    toDecimal(
      dinero({
        amount: product?.order_quantity_multiple || 1,
        currency: STOCK_UI,
      })
    )
  );
  const minOrderQty = Number(
    toDecimal(
      dinero({
        amount: product?.minimum_order_quantity || 1,
        currency: STOCK_UI,
      })
    )
  );
  const maxStock = Number(
    toDecimal(dinero({ amount: product?.stock ?? 0, currency: STOCK_UI }))
  );

  const maxStockGanjil =
    Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple;
  const maxStockGenap =
    Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple;
  const maxAllowedQuantity =
    isEven(orderQtyMultiple) || orderQtyMultiple === 1
      ? maxStockGanjil
      : maxStockGenap;
  const precision = orderQtyMultiple !== 1 ? 2 : 0;

  const normalizedDefaultQuantity = useMemo(() => {
    if (
      typeof defaultQuantity !== 'number' ||
      Number.isNaN(defaultQuantity) ||
      defaultQuantity <= 0
    ) {
      return minOrderQty;
    }
    const maxTarget =
      maxAllowedQuantity > 0 ? maxAllowedQuantity : defaultQuantity;
    const clamped = Math.min(maxTarget, Math.max(defaultQuantity, minOrderQty));
    if (!orderQtyMultiple || orderQtyMultiple <= 0) return clamped;
    const multiples = Math.max(1, Math.round(clamped / orderQtyMultiple));
    return Math.min(
      Number((multiples * orderQtyMultiple).toFixed(precision)),
      maxTarget
    );
  }, [
    defaultQuantity,
    minOrderQty,
    maxAllowedQuantity,
    orderQtyMultiple,
    precision,
  ]);

  useEffect(() => {
    if (normalizedDefaultQuantity > 0 && magnifiedQty === 0) {
      setUiQuantity(normalizedDefaultQuantity);
    }
  }, [normalizedDefaultQuantity, magnifiedQty]);

  const { inputProps, incProps, decProps, hasFractionalStep } =
    useStockNumberInput({
      id: `input-qty-modal-${productId}`,
      name: `input-qty-modal-${productId}`,
      valueUi: uiQuantity || normalizedDefaultQuantity,
      stepUi: orderQtyMultiple,
      minUi: minOrderQty,
      maxUi: maxAllowedQuantity,
      stepMagnified: product?.order_quantity_multiple || 1,
      roundOnBlurOnly: true,
      isDisabled:
        minOrderQty > maxStock || product?.stock_status === 'OUT_OF_STOCK',
      onChangeMagnifiedImmediate: (magnified) => {
        setMagnifiedQty(magnified);
        setUiQuantity(magnified / 1000);
      },
      onChangeMagnified: (magnified) => {
        setMagnifiedQty(magnified);
        setUiQuantity(magnified / 1000);
      },
      onError: (errorMessage) => {
        showToast(toast, 'error', errorMessage, undefined, 'bottom', 3000);
      },
    });

  const effectiveMagnifiedQty =
    magnifiedQty || normalizedDefaultQuantity * 1000;
  const promoOffers = useMemo(
    () => buildProductPromoOffers(product),
    [product]
  );

  const isPromoVoucherPreviewEnabled = usePromoVoucherPreviewEnabled();
  const isVoucherDisplayEnabled = useVoucherDisplayEnabled();
  const { data: voucherData } = useProductVoucherEligibilityList({
    itemCode: product?.item_code,
    size: 5,
    enabled:
      isOpen &&
      isPromoVoucherPreviewEnabled &&
      isVoucherDisplayEnabled &&
      hasCountVouchers(product),
  });
  const voucherOffers = useMemo(
    () => voucherData?.pages.flatMap((p) => p.data) ?? [],
    [voucherData]
  );

  const handleUseQuantity = (targetQuantity: number) => {
    if (targetQuantity > maxAllowedQuantity * 1000) return;
    setMagnifiedQty(targetQuantity);
    setUiQuantity(targetQuantity / 1000);
  };

  // Upsell analytics (Solusi Hemat! / Voucher Tersedia)
  const upsellItemParams = useMemo(
    () =>
      getItemCardTrackingParams(
        product,
        uiQuantity || minOrderQty,
        section,
        page,
        sectionId,
        undefined,
        {
          ...(itemIndex !== undefined ? { index: itemIndex } : {}),
          ...(sectionIndex !== undefined ? { section_index: sectionIndex } : {}),
        }
      ),
    [product, uiQuantity, minOrderQty, section, page, sectionId, itemIndex, sectionIndex]
  );

  const promoUpsellViewed = useRef(false);
  const voucherUpsellViewedCodes = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      promoUpsellViewed.current = false;
      voucherUpsellViewedCodes.current = new Set();
      return;
    }
    if (
      isPromoVoucherPreviewEnabled &&
      promoOffers.length > 0 &&
      !promoUpsellViewed.current
    ) {
      promoUpsellViewed.current = true;
      trackEvent(
        TRACKING_EVENTS.VIEW_PROMO_UPSELL,
        buildPromoUpsellParams(upsellItemParams, product)
      );
    }
    if (isPromoVoucherPreviewEnabled && isVoucherDisplayEnabled) {
      voucherOffers.forEach((voucher) => {
        if (voucherUpsellViewedCodes.current.has(voucher.code)) return;
        voucherUpsellViewedCodes.current.add(voucher.code);
        trackEvent(
          TRACKING_EVENTS.VIEW_VOUCHER_UPSELL,
          buildVoucherUpsellParams(upsellItemParams, product, voucher)
        );
        trackEvent(TRACKING_EVENTS.VIEW_VOUCHER, {
          ...getVoucherTrackingParams(voucher),
          page_name: page || section || '-',
          section_name: section || '-',
          items: [upsellItemParams],
        });
      });
    }
  }, [
    isOpen,
    isPromoVoucherPreviewEnabled,
    isVoucherDisplayEnabled,
    promoOffers.length,
    voucherOffers,
    upsellItemParams,
    product,
    page,
    section,
  ]);

  const { data: cartTotalData, isFetching: isCartTotalFetching } =
    useCartTotalProductDetail(productId, effectiveMagnifiedQty, isOpen);

  const totalPrice = Math.round(
    cartTotalData?.finalPriceMagnify?.magnifiedValue || 0
  );

  const stockStatus = product?.stock_status ?? '';
  const available =
    maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'Available'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'Stok Terbatas'
      : 'Out of Stock';
  const statusColor =
    maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'azure'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'orange'
      : 'red';

  const {
    isLoading,
    error,
    success,
    cartSnapshot,
    handleAddToCart,
    handleClose,
  } = useAddToCart({
    productId,
    token,
    selectedShipToId,
    product,
    section,
    page,
    sectionId,
    itemIndex,
    sectionIndex,
    magnifiedQty,
    normalizedDefaultQuantity,
    totalPrice,
    enableCartStoreSync,
    shouldAutoSelectOnAdd,
    autoSelectProductId,
    onSuccess,
    onClose,
    onSyncStart,
    onSyncComplete,
    isRecommendationEnabled: isCartRecommendationEnabled,
  });

  const { setProductDetails } = useCartStore((state) => ({
    setProductDetails: state.setProductDetails,
  }));

  useEffect(() => {
    if (product) {
      setProductDetails(productId, {
        basePrice: product?.prices?.[0]?.price || 0,
        discount_promos:
          product?.discount_promos?.map((promo) => ({
            id: promo.id,
            type: promo.is_discount ? 'PERCENTAGE' : 'FIXED_AMOUNT',
            value: promo.deduction_amount,
          })) || [],
      });
    }
  }, [product, productId, setProductDetails]);

  const successWithRecommendation = success && isCartRecommendationEnabled;
  const modalSize = isMobile ? 'sm' : successWithRecommendation ? '2xl' : 'lg';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size={modalSize}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        {!success && (
          <ModalHeader fontSize="xl" fontWeight="medium" pb={2}>
            Atur Jumlah Pesanan
          </ModalHeader>
        )}
        {!success && <ModalCloseButton size="lg" />}
        <ModalBody>
          {success ? (
            <SuccessContent
              isCartRecommendationEnabled={isCartRecommendationEnabled}
              productTitle={product?.desc || product?.title}
              addedItemCode={addedItemCode}
              cartSnapshot={cartSnapshot}
              onClose={handleClose}
              isMobile={isMobile}
              page={page}
            />
          ) : (
            <>
              <ProductInfoSection
                product={product}
                hasFractionalStep={hasFractionalStep}
                inputProps={inputProps}
                incProps={incProps}
                decProps={decProps}
                isCartTotalFetching={isCartTotalFetching}
                isLoading={isLoading}
                available={available}
                statusColor={statusColor}
              />

              {isPromoVoucherPreviewEnabled && (
                <PromoSection
                  offers={promoOffers}
                  effectiveMagnifiedQty={effectiveMagnifiedQty}
                  maxAllowedQuantity={maxAllowedQuantity}
                  onUseQuantity={(qty) => {
                    trackEvent(
                      TRACKING_EVENTS.SELECT_PROMO_UPSELL,
                      buildPromoUpsellParams(upsellItemParams, product)
                    );
                    handleUseQuantity(qty);
                  }}
                />
              )}

              {isPromoVoucherPreviewEnabled && isVoucherDisplayEnabled && (
                <VoucherSection
                  vouchers={voucherOffers}
                  itemCode={product?.item_code}
                  effectiveMagnifiedQty={effectiveMagnifiedQty}
                  maxAllowedQuantity={maxAllowedQuantity}
                  onUseQuantity={handleUseQuantity}
                  onSelectVoucher={(voucher) => {
                    trackEvent(
                      TRACKING_EVENTS.SELECT_VOUCHER_UPSELL,
                      buildVoucherUpsellParams(
                        upsellItemParams,
                        product,
                        voucher
                      )
                    );
                    trackEvent(TRACKING_EVENTS.SELECT_VOUCHER, {
                      ...getVoucherTrackingParams(voucher),
                      page_name: page || section || '-',
                      section_name: section || '-',
                    });
                  }}
                />
              )}

              <TotalPriceSection
                savedPrice={
                  cartTotalData?.savedPriceMagnify?.magnifiedValue ?? 0
                }
                originalPrice={
                  cartTotalData?.originalPriceMagnify?.magnifiedValue ?? 0
                }
                error={error}
              />
            </>
          )}
        </ModalBody>
        {!success && (
          <ModalFooter gap={3} pt={0}>
            <Skeleton
              borderRadius="lg"
              isLoaded={!isCartTotalFetching}
              flex="1"
            >
              <Box
                py={2.5}
                px={2}
                bgColor="#FAFAFA"
                rounded="lg"
                minH="40px"
                display="flex"
                alignItems="center"
              >
                <Flex align="baseline" gap={1}>
                  <Text color="#0000A3" fontWeight="medium" fontSize="sm">
                    Rp
                  </Text>
                  <Text color="#0000A3" fontWeight="medium" fontSize="xl">
                    {buyerCurrencyFormatter(
                      cartTotalData?.finalPriceMagnify?.magnifiedValue ?? 0
                    ).replace(/^Rp\s*/i, '')}
                  </Text>
                </Flex>
              </Box>
            </Skeleton>
            <Button
              w="96px"
              bgColor="#0000A3"
              color="white"
              onClick={handleAddToCart}
              isDisabled={
                isCartTotalFetching ||
                isLoading ||
                product?.stock_status === 'OUT_OF_STOCK' ||
                minOrderQty > maxStock
              }
              isLoading={isLoading || isCartTotalFetching}
              borderRadius="4px"
              height="40px"
              fontSize="xs"
              fontWeight="semibold"
              _hover={{ bgColor: '#000080' }}
            >
              + Keranjang
            </Button>
          </ModalFooter>
        )}
        {successWithRecommendation && (
          <ModalFooter gap={3} pt={2} justifyContent="flex-end">
            <Button
              variant="outline"
              borderColor="#0000A3"
              color="#0000A3"
              onClick={() => (window.location.href = '/cart')}
              borderRadius="8px"
              height="48px"
              fontSize="sm"
              fontWeight="medium"
              px={6}
            >
              Lihat Keranjang
            </Button>
            <Button
              bgColor="#0000A3"
              color="white"
              onClick={handleClose}
              borderRadius="8px"
              height="48px"
              fontSize="sm"
              fontWeight="medium"
              px={6}
              _hover={{ bgColor: '#000080' }}
            >
              Lanjut Belanja
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
